import React, { useState } from 'react';
import { Cell, flexRender, Row, VisibilityState } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import { Pressable } from 'react-native';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';

interface Props<T> {
  /**
   * Memo-bust props: the row renders its cells via flexRender, so its own props
   * are otherwise stable. These change when columns reorder / toggle and when
   * the group's selection changes, forcing React.memo to re-render (e.g. so the
   * parent's group checkbox updates on select-all). Read by getVisibleCells /
   * the cell closures, not directly here.
   */
  columnOrder?: string[];
  columnVisibility?: VisibilityState;
  index: number;
  isDeltaColumn: (columnId: string) => boolean;
  isLeftAlignedColumn: (columnId: string) => boolean;
  row: Row<T>;
  selectionSignature?: string;
  testID?: string;
}

/**
 * Parent group row for the Bookmarked table. Mirrors the review table's group
 * header but is neither sticky nor collapsible — bookmarked accounts are always
 * shown, so the row just renders its cells.
 */
const PricingBookmarkGroupRowImpl = <T,>({
  index,
  isDeltaColumn,
  isLeftAlignedColumn,
  row,
  testID,
}: Props<T>): React.ReactElement => {
  const [styles] = useStyles();
  const [alignmentStyles] = usePricingCellAlignmentStyles();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={styles.pressable}
      tabIndex={-1}
      testID={testID}
    >
      <TableRow index={index} key={row.id}>
        {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
          const alignmentStyle = getCellAlignmentStyle({
            isDelta: isDeltaColumn(cell.column.id),
            isLeftAligned: isLeftAlignedColumn(cell.column.id),
            styles: alignmentStyles,
          });
          return (
            <TableCell
              cell={cell}
              key={cell.id}
              style={{
                ...alignmentStyle,
                ...(isHovered ? styles.hovered : null),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
    </Pressable>
  );
};

export const PricingBookmarkGroupRow = React.memo(
  PricingBookmarkGroupRowImpl,
) as typeof PricingBookmarkGroupRowImpl;

const useStyles = createStyles((theme) => ({
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  pressable: {
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 2,
  },
}));
